import { useState, useEffect, useCallback, useMemo } from "react";
import bookingService from "../services/bookingService";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../config/firebase";

/* ── Timezone-safe date string (uses local date parts, not UTC) ───────────── */
function localDateStr(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/* ── Default slot definitions (mirrors adminCalendarService/AvailabilityCalendar) ── */
function getDefaultSlots(serviceId) {
  switch (serviceId) {
    case "jet-ski":
      return [
        { time: "09:00 - 10:00", capacity: 2, booked: 0 },
        { time: "11:00 - 12:00", capacity: 2, booked: 0 },
        { time: "16:00 - 17:00", capacity: 2, booked: 0 },
        { time: "18:00 - 19:00", capacity: 2, booked: 0 },
      ];
    case "luxury-yacht":
      return [
        { time: "10:00 - 14:00", capacity: 10, booked: 0 },
        { time: "16:00 - 20:00", capacity: 10, booked: 0 },
      ];
    case "bayliner":
      return [
        { time: "09:00 - 09:30", capacity: 5, booked: 0 },
        { time: "09:00 - 10:00", capacity: 5, booked: 0 },
        { time: "10:00 - 10:30", capacity: 5, booked: 0 },
        { time: "11:00 - 11:30", capacity: 5, booked: 0 },
        { time: "11:00 - 12:00", capacity: 5, booked: 0 },
        { time: "14:00 - 14:30", capacity: 5, booked: 0 },
        { time: "14:00 - 15:00", capacity: 5, booked: 0 },
        { time: "15:00 - 15:30", capacity: 5, booked: 0 },
        { time: "16:00 - 16:30", capacity: 5, booked: 0 },
        { time: "16:00 - 17:00", capacity: 5, booked: 0 },
        { time: "17:00 - 17:30", capacity: 5, booked: 0 },
      ];
    default: // speedboat — all possible slots across all 4 packages
      return [
        { time: "09:00 - 10:00", capacity: 11, booked: 0 },
        { time: "09:00 - 10:30", capacity: 11, booked: 0 },
        { time: "09:00 - 11:00", capacity: 11, booked: 0 },
        { time: "11:00 - 12:00", capacity: 11, booked: 0 },
        { time: "15:00 - 16:00", capacity: 11, booked: 0 },
        { time: "17:00 - 18:30", capacity: 11, booked: 0 },
        { time: "18:00 - 19:00", capacity: 11, booked: 0 },
        { time: "18:00 - 20:00", capacity: 11, booked: 0 },
      ];
  }
}

/**
 * Custom hook for managing bookings with real-time updates
 */
export const useBooking = (serviceId = null) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serviceId) return;

    // Set up real-time listener for bookings
    const bookingsQuery = query(
      collection(db, "bookings"),
      where("serviceId", "==", serviceId),
    );

    const unsubscribe = onSnapshot(
      bookingsQuery,
      (snapshot) => {
        const bookingsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBookings(bookingsData);
      },
      (err) => {
        console.error("Error listening to bookings:", err);
        setError(err.message);
      },
    );

    return () => unsubscribe();
  }, [serviceId]);

  const createBooking = useCallback(async (bookingData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.createBooking(bookingData);
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  const cancelBooking = useCallback(async (bookingId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.updateBookingStatus(
        bookingId,
        "cancelled",
      );
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  const confirmBooking = useCallback(async (bookingId, paymentId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.updateBookingStatus(
        bookingId,
        "confirmed",
        paymentId,
      );
      setLoading(false);
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  return {
    bookings,
    loading,
    error,
    createBooking,
    cancelBooking,
    confirmBooking,
  };
};

/**
 * Custom hook for checking availability with real-time updates.
 * Subscribes to availability docs, bookings, externalBookings, and blockedDates.
 * Computes real booked counts from live bookings so stale counters never appear.
 */
export const useAvailability = (serviceId, startDate, endDate) => {
  const [availDocs, setAvailDocs] = useState([]);
  const [regBookings, setRegBookings] = useState([]);
  const [extBookings, setExtBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startStr = localDateStr(startDate);
  const endStr = localDateStr(endDate);

  // 1. Subscribe to availability docs — query by date range only (no composite index needed).
  //    Filter by serviceId client-side so a simple single-field auto-index is sufficient.
  useEffect(() => {
    if (!serviceId || !startStr || !endStr) {
      setAvailDocs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, "availability"),
      where("date", ">=", startStr),
      where("date", "<=", endStr),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAvailDocs(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((a) => a.serviceId === serviceId),
        );
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to availability:", err);
        setError(err.message);
        setAvailDocs([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [serviceId, startStr, endStr]);

  // 2. Subscribe to regular bookings — date range only, filter by serviceId client-side.
  useEffect(() => {
    if (!serviceId || !startStr || !endStr) {
      setRegBookings([]);
      return;
    }
    const q = query(
      collection(db, "bookings"),
      where("date", ">=", startStr),
      where("date", "<=", endStr),
    );
    const unsub = onSnapshot(
      q,
      (snap) =>
        setRegBookings(
          snap.docs
            .map((d) => d.data())
            .filter((b) => b.serviceId === serviceId),
        ),
      (err) => {
        console.error("Error listening to bookings:", err);
        setRegBookings([]);
      },
    );
    return () => unsub();
  }, [serviceId, startStr, endStr]);

  // 3. Subscribe to external bookings in date range (filter by serviceId client-side)
  useEffect(() => {
    if (!serviceId || !startStr || !endStr) {
      setExtBookings([]);
      return;
    }
    const q = query(
      collection(db, "externalBookings"),
      where("date", ">=", startStr),
      where("date", "<=", endStr),
    );
    const unsub = onSnapshot(
      q,
      (snap) =>
        setExtBookings(
          snap.docs
            .map((d) => d.data())
            .filter((b) => b.serviceId === serviceId),
        ),
      (err) => {
        console.error("Error listening to external bookings:", err);
        setExtBookings([]);
      },
    );
    return () => unsub();
  }, [serviceId, startStr, endStr]);

  // 4. Subscribe to blockedDates — any block for this service or "all" services
  useEffect(() => {
    if (!serviceId || !startStr || !endStr) {
      setBlockedDates(new Set());
      return;
    }
    const q = query(
      collection(db, "blockedDates"),
      where("date", ">=", startStr),
      where("date", "<=", endStr),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const blocked = new Set();
        snap.docs.forEach((d) => {
          const data = d.data();
          // Block if it applies to this service specifically or to all services
          if (data.serviceId === serviceId || data.serviceId === "all") {
            blocked.add(data.date);
          }
        });
        setBlockedDates(blocked);
      },
      (err) => {
        console.error("Error listening to blocked dates:", err);
        setBlockedDates(new Set());
      },
    );
    return () => unsub();
  }, [serviceId, startStr, endStr]);

  // 5. Merge: compute real booked counts from live booking docs.
  //    Uses default slot structure for dates that have bookings but no availability doc yet.
  const availability = useMemo(() => {
    const active = [
      ...regBookings.filter((b) => b.status !== "cancelled"),
      ...extBookings.filter((b) => b.status !== "cancelled"),
    ];

    // Separate slotted vs unslotted bookings.
    // External bookings added without a time slot would otherwise never match
    // any slot time string, making the calendar show "Open" even with bookings.
    const slottedMap = {}; // key: "date__timeSlot"
    const unslottedByDate = {}; // key: date — bookings with no timeSlot
    active.forEach((b) => {
      const guests = Number(b.guestCount) || Number(b.numberOfGuests) || 0;
      if (!guests) return;
      if (b.timeSlot) {
        const key = `${b.date}__${b.timeSlot}`;
        slottedMap[key] = (slottedMap[key] || 0) + guests;
      } else {
        unslottedByDate[b.date] = (unslottedByDate[b.date] || 0) + guests;
      }
    });

    // Index availability docs by date
    const byDate = {};
    availDocs.forEach((a) => {
      byDate[a.date] = a;
    });

    // For dates with bookings but no availability doc yet, create a synthetic entry
    // using the service's default slot structure so status can be computed correctly.
    const defSlots = getDefaultSlots(serviceId);
    const defCap = defSlots.reduce((s, sl) => s + sl.capacity, 0);
    active.forEach((b) => {
      if (b.date && !byDate[b.date]) {
        byDate[b.date] = {
          date: b.date,
          serviceId,
          timeSlots: defSlots.map((s) => ({ ...s })),
          totalCapacity: defCap,
          bookedCapacity: 0,
          _synthetic: true,
        };
      }
    });

    return Object.values(byDate)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((a) => {
        // Always use default slot structure; merge slotted booking counts
        const baseSlots = a.timeSlots?.length
          ? a.timeSlots
          : defSlots.map((s) => ({ ...s }));
        let updatedSlots = baseSlots.map((s) => ({
          ...s,
          booked: slottedMap[`${a.date}__${s.time}`] || 0,
        }));
        // Distribute unslotted bookings to the first slot with remaining capacity
        // so the calendar correctly reflects external bookings added without a time slot.
        const unslotted = unslottedByDate[a.date] || 0;
        if (unslotted > 0 && updatedSlots.length > 0) {
          const idx = updatedSlots.findIndex((s) => s.booked < s.capacity);
          const target = idx >= 0 ? idx : 0;
          updatedSlots = updatedSlots.map((s, i) =>
            i === target ? { ...s, booked: s.booked + unslotted } : s,
          );
        }
        const realBooked = updatedSlots.reduce((sum, s) => sum + s.booked, 0);
        return { ...a, timeSlots: updatedSlots, bookedCapacity: realBooked };
      });
  }, [availDocs, regBookings, extBookings, serviceId]);

  const checkAvailability = useCallback(
    async (date, timeSlot, numberOfGuests) => {
      setLoading(true);
      setError(null);
      try {
        const result = await bookingService.checkAvailability(
          serviceId,
          date,
          timeSlot,
          numberOfGuests,
        );
        setLoading(false);
        return result;
      } catch (err) {
        setError(err.message);
        setLoading(false);
        throw err;
      }
    },
    [serviceId],
  );

  return { availability, blockedDates, loading, error, checkAvailability };
};

/**
 * Custom hook for user bookings
 */
export const useUserBookings = (userId) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Set up real-time listener
    const bookingsQuery = query(
      collection(db, "bookings"),
      where("userId", "==", userId),
    );

    const unsubscribe = onSnapshot(
      bookingsQuery,
      (snapshot) => {
        const bookingsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBookings(bookingsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to user bookings:", err);
        setError(err.message);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userId]);

  return { bookings, loading, error };
};
